import { Injectable } from '@angular/core';
import { LanguageService } from 'src/app/multi-language/language.service';
import { TranslateService } from '@ngx-translate/core';
import { TfService } from 'src/app/tf/tf-service.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';

/**
 * Maps the app's language codes to BCP 47 locale tags for Luxon.
 * Used as fallback when browser locale is not available.
 */
const APP_LANGUAGE_TO_LOCALE: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  De: 'de-DE',
  de: 'de-DE',
  // tn: 'fr-TN',
  // 'fr-TN': 'fr-TN',
  // ar: 'ar-TN',
  // 'ar-TN': 'ar-TN',
};

const DEFAULT_LOCALE = 'en-US';

/** Luxon date preset keys (from DateTime). */
const LUXON_DATE_PRESET_KEYS: string[] = [
  'DATE_SHORT',
  'DATE_MED',
  'DATE_MED_WITH_WEEKDAY',
  'DATE_FULL',
  'DATE_HUGE',
];

/**
 * Locale variations offered in the date format selector.
 */
const DATE_FORMAT_LOCALES: string[] = [
  'en-US',
  'en-GB',
  'en-AU',
  'en-CA',
  'fr-FR',
  'fr-TN',
  'ar-TN',
  'de-DE',
  'de-AT',
  'de-CH',
  'es-ES',
  'es-MX',
  'it-IT',
  'pt-BR',
  'pt-PT',
  'nl-NL',
  'pl-PL',
  'ru-RU',
  'ja-JP',
  'zh-CN',
  'zh-TW',
  'ko-KR',
  'ar-SA',
  'tr-TR',
  'sv-SE',
  'da-DK',
  'no-NB',
  'fi-FI',
  'el-GR',
  'hu-HU',
  'cs-CZ',
  'ro-RO',
];

const PRESET_LOCALE_SEPARATOR = ':';

const PREFERRED_DATE_FORMAT_STORAGE_KEY = 'preferredDateFormat';

const PRESET_LABEL_KEYS: Record<string, string> = {
  DATE_SHORT: 'TFUsersTranslate.DateFormatShort',
  DATE_MED: 'TFUsersTranslate.DateFormatMedium',
  DATE_MED_WITH_WEEKDAY: 'TFUsersTranslate.DateFormatMediumWeekday',
  DATE_FULL: 'TFUsersTranslate.DateFormatFull',
  DATE_HUGE: 'TFUsersTranslate.DateFormatFullWeekday',
};

export type DateFormatOption =
  | { value: string; labelKey: string }
  | { value: string; presetLabelKey: string; localeLabelKey: string };

export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  { value: '', labelKey: 'TFUsersTranslate.DateFormatUseLocale' },
  ...LUXON_DATE_PRESET_KEYS.flatMap((preset) =>
    preset in DateTime
      ? DATE_FORMAT_LOCALES.map((locale) => {
          const localeSuffix = locale.replace('-', '');
          return {
            value: `${preset}${PRESET_LOCALE_SEPARATOR}${locale}`,
            presetLabelKey: PRESET_LABEL_KEYS[preset] ?? 'TFUsersTranslate.DateFormatShort',
            localeLabelKey: `TFUsersTranslate.Locale_${localeSuffix}`,
          } as DateFormatOption;
        })
      : []
  ),
];

/**
 * Service that provides the active date format for the application using Luxon.
 * Priority:
 * 1. Custom format from user profile (preferred_date_format)
 * 2. Locale from user localization (browser locale), then app language → Luxon locale-based formatting
 */
@Injectable({
  providedIn: 'root',
})
export class DataFormatService {
  private profileDateFormat: string | null = null;
  private profileFormatLoaded = false;
  private dateFormatSubject = new BehaviorSubject<string | null>(null);
  private localeSubject = new BehaviorSubject<string>(DEFAULT_LOCALE);

  constructor(
    private languageService: LanguageService,
    private translate: TranslateService,
    private tfService: TfService
  ) {
    this.loadFromLocalStorage();
    this.emitChange();
    this.languageService?.currentLanguage$?.subscribe(() => this.onLocaleChange());
    this.translate?.onLangChange?.subscribe(() => this.onLocaleChange());
    this.tfService?.authInfo$?.subscribe((auth) => {
      if (!auth?.isAuthenticated) {
        this.clearLocalStorageAndReset();
      }
    });
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(PREFERRED_DATE_FORMAT_STORAGE_KEY);
      if (stored != null && String(stored).trim() !== '') {
        this.profileDateFormat = String(stored).trim();
      }
    } catch {
      // ignore
    }
  }

  private saveToLocalStorage(): void {
    try {
      if (this.profileDateFormat != null && this.profileDateFormat.trim() !== '') {
        localStorage.setItem(PREFERRED_DATE_FORMAT_STORAGE_KEY, this.profileDateFormat);
      } else {
        localStorage.removeItem(PREFERRED_DATE_FORMAT_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }

  private clearLocalStorageAndReset(): void {
    this.profileDateFormat = null;
    this.profileFormatLoaded = false;
    try {
      localStorage.removeItem(PREFERRED_DATE_FORMAT_STORAGE_KEY);
    } catch {
      // ignore
    }
    this.emitChange();
  }

  getDateFormat(): string | null {
    return this.profileDateFormat || null;
  }

  getLocale(): string {
    return this.getEffectiveLocale();
  }

  /**
   * Formats a date using the active format (profile format or locale).
   * Value can be: "" (use locale), "PRESET:LOCALE" (e.g. DATE_SHORT:en-US), or a Luxon format string.
   */
  formatDate(value: Date | string | number, formatOverride?: string): string | null {
    const dt = this.toDateTime(value);
    if (!dt || !dt.isValid) return null;
    const format = formatOverride ?? this.getDateFormat();
    if (!format) {
      return dt.setLocale(this.getLocale()).toLocaleString(DateTime.DATE_SHORT);
    }
    const sep = format.indexOf(PRESET_LOCALE_SEPARATOR);
    if (sep !== -1) {
      const presetKey = format.slice(0, sep);
      const locale = format.slice(sep + 1);
      const preset =
        presetKey in DateTime
          ? (DateTime as Record<string, Intl.DateTimeFormatOptions>)[presetKey]
          : null;
      if (preset) {
        return dt.setLocale(locale).toLocaleString(preset);
      }
    }
    return dt.toFormat(format);
  }

  get dateFormat$(): Observable<string | null> {
    return this.dateFormatSubject.asObservable();
  }

  get locale$(): Observable<string> {
    return this.localeSubject.asObservable();
  }

  setProfileDateFormat(format: string | null | undefined): void {
    this.profileDateFormat = format && format.trim() !== '' ? format : null;
    this.profileFormatLoaded = true;
    this.saveToLocalStorage();
    this.emitChange();
  }

  ensureProfileFormatLoaded(): Observable<string | null> {
    if (this.profileFormatLoaded) {
      return of(this.getDateFormat());
    }
    const user = this.tfService?.getUserFromLocalCache?.();
    if (!this.tfService || !user?.['userId']) {
      this.profileFormatLoaded = true;
      return of(this.getDateFormat());
    }
    return this.tfService.profile(user['userId']).pipe(
      tap((data: { preferred_date_format?: string }) => {
        const format = data?.preferred_date_format ?? null;
        this.profileDateFormat = format && String(format).trim() !== '' ? String(format) : null;
        this.profileFormatLoaded = true;
        this.saveToLocalStorage();
        this.emitChange();
      }),
      map(() => this.getDateFormat()),
      catchError(() => {
        this.profileFormatLoaded = true;
        return of(this.getDateFormat());
      })
    );
  }

  getLocaleForLanguage(lang: string): string {
    if (!lang) return DEFAULT_LOCALE;
    const normalized = lang.split('-')[0];
    return APP_LANGUAGE_TO_LOCALE[normalized] ?? APP_LANGUAGE_TO_LOCALE[lang] ?? DEFAULT_LOCALE;
  }

  toDateTime(value: Date | string | number): DateTime | null {
    if (value == null) return null;
    let dt: DateTime;
    if (value instanceof Date) {
      dt = DateTime.fromJSDate(value);
    } else if (typeof value === 'number') {
      dt = DateTime.fromMillis(value);
    } else {
      dt = DateTime.fromISO(String(value));
      if (!dt.isValid) {
        dt = DateTime.fromJSDate(new Date(value));
      }
    }
    return dt.isValid ? dt : null;
  }

  /**
   * Returns the locale for date formatting based on user localization (browser locale first),
   * then app language, then default.
   */
  private getEffectiveLocale(): string {
    if (typeof navigator !== 'undefined' && navigator.language) {
      const browserLocale = navigator.language;
      if (browserLocale && browserLocale.length >= 2) {
        const normalized = browserLocale.trim();
        if (normalized) return normalized;
      }
    }
    const userLang = this.languageService?.getCurrentLanguage?.();
    if (userLang) {
      const locale = this.getLocaleForLanguage(userLang);
      if (locale !== DEFAULT_LOCALE) return locale;
    }
    const requestLang = this.translate?.currentLang || this.translate?.defaultLang;
    if (requestLang) {
      return this.getLocaleForLanguage(requestLang);
    }
    return DEFAULT_LOCALE;
  }

  private getEffectiveFormat(): string | null {
    return this.profileDateFormat && this.profileDateFormat.trim() !== ''
      ? this.profileDateFormat
      : null;
  }

  private onLocaleChange(): void {
    this.emitChange();
  }

  private emitChange(): void {
    this.dateFormatSubject.next(this.getEffectiveFormat());
    this.localeSubject.next(this.getEffectiveLocale());
  }
}
